import { STORAGE_KEYS } from "../config/constants";
import {
  getPluginModelPickerSnapshot,
  subscribePluginModelPicker,
} from "../featureFlags/pluginModelPicker";
import {
  findModelPickerEntry,
  type ModelPickerEntry,
  type ModelPickerHost,
  type ModelThinkingEffort,
  type OpenAIReasoningMode,
} from "./catalog";

const STORAGE_VERSION = 1;

export interface ModelPickerSelection {
  readonly modelId: string;
  readonly effort: ModelThinkingEffort;
  readonly mode?: OpenAIReasoningMode;
}

export interface ResolvedModelPickerOverride extends ModelPickerSelection {
  readonly entry: ModelPickerEntry;
}

interface PersistedModelPickerState {
  version: typeof STORAGE_VERSION;
  accounts: Record<string, Partial<Record<ModelPickerHost, ModelPickerSelection>>>;
}

type ModelPickerSelectionListener = () => void;

const EMPTY_STATE: PersistedModelPickerState = {
  version: STORAGE_VERSION,
  accounts: {},
};

const listeners = new Set<ModelPickerSelectionListener>();
let revision = 0;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readPersistedState(): PersistedModelPickerState {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.MODEL_PICKER_OVERRIDES);
    if (!raw) return EMPTY_STATE;
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed) || parsed.version !== STORAGE_VERSION || !isRecord(parsed.accounts)) {
      return EMPTY_STATE;
    }
    return {
      version: STORAGE_VERSION,
      accounts: parsed.accounts as PersistedModelPickerState["accounts"],
    };
  } catch {
    return EMPTY_STATE;
  }
}

function writePersistedState(state: PersistedModelPickerState): void {
  try {
    localStorage.setItem(STORAGE_KEYS.MODEL_PICKER_OVERRIDES, JSON.stringify(state));
  } catch {
    // Storage can be unavailable in private/locked-down webviews. The picker
    // safely falls back to host defaults rather than making persistence fatal.
  }
}

function emitChange(): void {
  revision += 1;
  listeners.forEach((listener) => listener());
}

function normalizeSelection(
  host: ModelPickerHost,
  value: unknown
): ResolvedModelPickerOverride | null {
  if (!isRecord(value) || typeof value.modelId !== "string") return null;
  const entry = findModelPickerEntry(host, value.modelId);
  if (!entry) return null;

  const effort = entry.allowedEfforts.includes(value.effort as ModelThinkingEffort)
    ? (value.effort as ModelThinkingEffort)
    : entry.defaultEffort;
  const allowedModes = entry.allowedModes ?? [];
  const mode =
    allowedModes.length > 0
      ? allowedModes.includes(value.mode as OpenAIReasoningMode)
        ? (value.mode as OpenAIReasoningMode)
        : (entry.defaultMode ?? allowedModes[0])
      : undefined;

  return {
    entry,
    modelId: entry.id,
    effort,
    ...(mode ? { mode } : {}),
  };
}

function getActiveAccountScopeKey(): string | null {
  const flag = getPluginModelPickerSnapshot();
  if (!flag.ready || !flag.enabled) return null;
  return flag.accountScopeKey.trim() || null;
}

export function resolveModelPickerOverride(
  host: ModelPickerHost
): ResolvedModelPickerOverride | null {
  const accountScopeKey = getActiveAccountScopeKey();
  if (!accountScopeKey) return null;
  const persisted = readPersistedState();
  return normalizeSelection(host, persisted.accounts[accountScopeKey]?.[host]);
}

export function setModelPickerOverride(
  host: ModelPickerHost,
  selection: ModelPickerSelection | null
): void {
  const accountScopeKey = getActiveAccountScopeKey();
  if (!accountScopeKey) return;

  const persisted = readPersistedState();
  const accountSelections = { ...(persisted.accounts[accountScopeKey] ?? {}) };
  const normalized = selection ? normalizeSelection(host, selection) : null;
  if (normalized) {
    accountSelections[host] = {
      modelId: normalized.modelId,
      effort: normalized.effort,
      ...(normalized.mode ? { mode: normalized.mode } : {}),
    };
  } else {
    delete accountSelections[host];
  }

  const accounts = { ...persisted.accounts };
  if (Object.keys(accountSelections).length > 0) {
    accounts[accountScopeKey] = accountSelections;
  } else {
    delete accounts[accountScopeKey];
  }
  writePersistedState({ version: STORAGE_VERSION, accounts });
  emitChange();
}

export function subscribeModelPickerSelection(
  listener: ModelPickerSelectionListener
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getModelPickerSelectionRevision(): number {
  return revision;
}

/** Test seam. Clears persisted picker overrides without touching other app state. */
export function resetModelPickerSelectionForTests(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.MODEL_PICKER_OVERRIDES);
  } catch {
    // Ignore unavailable storage in tests that exercise the safe fallback.
  }
  emitChange();
}

// Account/flag changes alter which persisted record is active.
subscribePluginModelPicker(emitChange);
